import { roundToPrecision } from '@orgbrain/lib-admin';
import { isNumericLike } from '@orgbrain/lib-data';
import { EmissionPlan } from '../classes/emission-plan';
import {
  DividendEvent,
  EditEvent,
  EmissionEvent,
  EstablishEvent,
  EventDataDividendSubtype,
  ImportEvent,
  moveSharesToClassEvent,
  ShareClass,
  ShareDeletionDataEvent,
  ShareholderEvent,
  ShareholderEventType,
  ShareholderParvalueChangeEventlist,
  ShareholderRecord,
  ShareholderWithStateEvent,
  ShareParvalueChangeEvent,
  SpliceEvent,
  SplitEvent,
  State,
  TransactionEvent,
} from '../classes/shared';
import {
  _delete,
  _edit,
  _emission,
  _establish,
  _import,
  _moveSharesFromShareclass,
  _refreshShareNumbers,
  _splice,
  _split,
  _transaction,
} from './statebuilders';

export class InconsistentEventsException extends Error {
  public override name: string;

  constructor(message: string) {
    super(message);
    this.name = 'InconsistentEventsException';
  }
}

export interface ValueHistoryElement {
  date: Date;
  value: number;
  price: number;
  shareAmount: number;
  volume: number;
}
type ShareClassId = string;

export class Events {
  public static snapshotExistsAfterDate(events: ShareholderEvent[], date: Date): boolean {
    return !!events.find(
      (event: ShareholderEvent): boolean =>
        new Date(event.date || event.createdAt) > new Date(date) &&
        (event.type === ShareholderEventType.import || event.type === ShareholderEventType.edit)
    );
  }

  public static sortEventsByDate(events: ShareholderEvent[]): ShareholderEvent[] {
    return events.sort((evt1, evt2) => {
      const date1 = new Date(evt1.date || evt1.createdAt);
      const date2 = new Date(evt2.date || evt2.createdAt);
      if (date1 > date2) {
        return 1;
      } else {
        return -1;
      }
    });
  }

  public static areEventsConsistent(events: ShareholderEvent[]): boolean {
    events = this.sortEventsByDate(events);

    const rest = events;

    if (rest.length > 0) {
      let state: State = {
        records: [],
        highestIssuedShareNumber: 0,
      }; // DEEP COPY
      try {
        for (const event of rest) {
          if (this.isStateConsistent(state)) {
            state = this.applyEventToState(event, state);
          } else {
            // This log is very useful for debugging inconsistent events
            console.error('Inconsistent state at event: ', event.date, ' state', state.records);

            return false;
          }
        }
      } catch (error: any) {
        console.log(error);
        return false;
      }
      return this.isStateConsistent(state);
    } else if (events.length === 0) {
      return true;
    } else {
      return false;
    }
  }

  public static areEventsConsistentWithNewEvent(
    event: ShareholderEvent,
    events: ShareholderEvent[]
  ): boolean {
    let newevents: ShareholderEvent[] = this.sortEventsByDate([...events, event]);
    if (event.type === ShareholderEventType.import) {
      // We skip the first events if this is an import
      newevents = newevents.filter((newEvent: ShareholderEvent) => {
        const date = new Date(newEvent.date || (newEvent as any).createdAt);
        return date >= new Date(event.date);
      });
    }
    const firstEvent = newevents.shift() as EmissionEvent | ImportEvent;

    newevents = [firstEvent, ...newevents];
    return this.areEventsConsistent(newevents);
  }

  public static isStateConsistent(state: State): boolean {
    return !state.records.find(({ amount }) => amount < 0);
  }

  public static replayedStateWithAdditionalEvent(
    event: ShareholderEvent,
    events: ShareholderEvent[]
  ): State {
    const newevents: ShareholderEvent[] = [...events, event];

    const replayedState: State = this.replay(newevents, event.organizationId);
    return replayedState;
  }
  //public static replayStateToEvent(){}

  public static replay(events: ShareholderEvent[], organizationId: string): State {
    const sortedevents = Events.sortEventsByDate(events);

    return Events.replayToEvent(sortedevents, sortedevents[events.length - 1], organizationId); // This replays to the last event in the list
  }
  public static replayAndCreateParvalueChangeEventList(
    shareclass: ShareClass,
    events: ShareholderEvent[]
  ): ShareholderParvalueChangeEventlist[] {
    const sortedevents = Events.sortEventsByDate(events);
    let parValue = isNumericLike(shareclass?.par_value) ? +shareclass.par_value : 0;
    const firstEventUsingShareClass = sortedevents.find(
      (event) =>
        ((event.type === ShareholderEventType.emission ||
          event.type === ShareholderEventType.edit ||
          event.type === ShareholderEventType.establish ||
          event.type === ShareholderEventType.import) &&
          (event as ShareholderWithStateEvent).state.records.some(
            (record) => record.shareclassId.toString() === shareclass._id.toString()
          )) ||
        (event.type === ShareholderEventType.moveShares &&
          (event as moveSharesToClassEvent).toShareclassId.toString() === shareclass._id.toString())
    );

    const firstEventDate = firstEventUsingShareClass ? firstEventUsingShareClass.date : null;
    if (!firstEventUsingShareClass) {
      return [];
    }
    const parvalueEventList: ShareholderParvalueChangeEventlist[] = [
      {
        eventId: '',
        eventType: 'originalValue',
        eventDate: sortedevents.length > 0 ? firstEventDate : new Date(),
        shareclassId: shareclass._id?.toString(),
        parValue: parValue,
      },
    ];
    // make a list of sorted events
    // for each shareclass
    // replay the events

    sortedevents.forEach((event) => {
      switch (event.type) {
        case ShareholderEventType.emission:
          break;
        case ShareholderEventType.split:
          // the parvalue that is current need to be updated to the new parvalue of the shareclass that  is being split into a for example 10:1 split
          if ((event as SplitEvent).shareclassId.toString() === shareclass._id.toString()) {
            const splitevent = event as SplitEvent;
            const ratio = splitevent.from / splitevent.to;
            parValue = precisionHelper(parValue * ratio);
            parvalueEventList.push({
              eventId: splitevent._id,
              eventType: splitevent.type,
              eventDate: splitevent.date,
              shareclassId: splitevent.shareclassId,
              parValue: parValue,
            });
          }
          break;

        case ShareholderEventType.splice:
          // the parvalue that is current need to be updated to the new parvalue of the shareclass that  is being split into a for example 10:1 split
          if ((event as SpliceEvent).shareclassId.toString() === shareclass._id.toString()) {
            const spliceevent = event as SpliceEvent;
            const ratio = spliceevent.from / spliceevent.to;

            parValue = precisionHelper(parValue * ratio);
            parvalueEventList.push({
              eventId: spliceevent._id,
              eventType: spliceevent.type,
              eventDate: spliceevent.date,
              shareclassId: spliceevent.shareclassId,
              parValue: parValue,
            });
          }
          break;
        case ShareholderEventType.establish:
          break;
        case ShareholderEventType.edit:
          break;
        case ShareholderEventType.sharedelete:
          break;
        case ShareholderEventType.import:
          break;
        case ShareholderEventType.dividend:
          //it is stock price that is reduced not par value
          if (
            (event as DividendEvent).subType ===
              EventDataDividendSubtype.Reduction_of_share_capital &&
            (event as DividendEvent).shareclassId.toString() === shareclass._id.toString()
          ) {
            parValue = parValue - (event as DividendEvent).dividend;
            parvalueEventList.push({
              eventId: event._id,
              eventType: (event as DividendEvent).subType,
              eventDate: event.date,
              shareclassId: shareclass._id?.toString(),
              parValue: parValue,
            });
          }
          break;
        case ShareholderEventType.parvaluechange:
          if (
            (event as ShareParvalueChangeEvent).shareclassId.toString() ===
            shareclass._id.toString()
          ) {
            const parvalueevent = event as ShareParvalueChangeEvent;
            parValue = parvalueevent.parValue;
            parvalueEventList.push({
              eventId: parvalueevent._id,
              eventType: parvalueevent.type,
              eventDate: parvalueevent.date,
              shareclassId: parvalueevent.shareclassId,
              parValue: parvalueevent.parValue,
            });
          }
          //eventId: string;
          //eventType: string;
          //eventDate: Date;
          //shareclassId: string;
          //parValue: number;
          break;
        default:
          break;
      }
    });

    return parvalueEventList; // This replays to the last event in the list}
  }

  public static replayCurrentParvalue(shareclass, events, returnDefaultValue = false) {
    const sortedevents = Events.sortEventsByDate(events);

    let parValue = isNumericLike(shareclass?.par_value) ? +shareclass.par_value : 0;

    for (const event of sortedevents) {
      switch (event.type) {
        case ShareholderEventType.emission:
          break;
        case ShareholderEventType.split:
          // the parvalue that is current need to be updated to the new parvalue of the shareclass that  is being split into a for example 10:1 split
          if ((event as SplitEvent).shareclassId.toString() === shareclass._id.toString()) {
            const splitevent = event as SplitEvent;
            const ratio = splitevent.from / splitevent.to;
            parValue = parValue * ratio;
          }
          break;

        case ShareholderEventType.splice:
          // the parvalue that is current need to be updated to the new parvalue of the shareclass that  is being split into a for example 10:1 split
          if ((event as SpliceEvent).shareclassId.toString() === shareclass._id.toString()) {
            const spliceevent = event as SpliceEvent;
            const ratio = spliceevent.from / spliceevent.to;

            parValue = parValue * ratio;
          }
          break;

        case ShareholderEventType.establish:
          break;
        case ShareholderEventType.edit:
          break;
        case ShareholderEventType.sharedelete:
          break;
        case ShareholderEventType.import:
          break;
        case ShareholderEventType.dividend:
          //it is stock price that is reduced not par value
          //  if (
          //    (event as DividendEvent).subType ===
          //      EventDataDividendSubtype.Reduction_of_share_capital &&
          //    (event as DividendEvent).shareclassId.toString() === shareclass._id.toString()
          //  ) {
          //    parValue = parValue - (event as DividendEvent).dividend;
          //  }
          break;
        case ShareholderEventType.parvaluechange:
          if (
            (event as ShareParvalueChangeEvent)?.shareclassId?.toString() ===
            shareclass?._id.toString()
          ) {
            const parvalueevent = event as ShareParvalueChangeEvent;
            parValue = parvalueevent.parValue;
          }

          break;
        default:
          break;
      }
    }
    parValue = precisionHelper(parValue);
    if (parValue === shareclass.par_value) {
      return returnDefaultValue ? parValue : null;
    }
    return parValue;
  }
  public static replayToEvent(
    events: ShareholderEvent[],
    eventLimit: ShareholderEvent,
    organizationId: string
  ): State {
    let state: State = new State();

    for (const event of events) {
      state = Events.applyEventToState(event, state);
      if (eventLimit._id === event._id) {
        break;
      }
    }
    state.organizationId = organizationId;
    state.records = this.removeEmptyRecords(state.records);

    return state;
  }

  public static listUntilIncludingEvent(
    events: ShareholderEvent[],
    eventLimit: ShareholderEvent
  ): ShareholderEvent[] {
    const list = [];

    for (const event of events) {
      list.push(event);
      if (eventLimit._id === event._id) {
        break;
      }
    }

    return list;
  }

  public static replayToDate(
    events: ShareholderEvent[],
    date: Date,
    throwIfNoticeEditEvent?: boolean
  ): State {
    let state: State = new State();
    for (const event of events) {
      if (
        throwIfNoticeEditEvent &&
        (event.type === ShareholderEventType.edit || event.type === ShareholderEventType.import)
      ) {
        throw new Error('throwIfNoticeEditEvent');
      }

      const eventdate = event.date || event.createdAt;
      if (new Date(date) < new Date(eventdate)) {
        break;
      }
      state = this.applyEventToState(event, state);
    }

    state.records = this.removeEmptyRecords(state.records);

    return state;
  }

  public static getEventsToDate(events: ShareholderEvent[], date: Date): ShareholderEvent[] {
    const list = [];
    for (const event of events) {
      const eventdate = event.date || event.createdAt;
      if (new Date(date) < new Date(eventdate)) {
        break;
      }
      list.push(event);
    }

    return list;
  }

  public static genState(events, date) {
    const fDate: Date = new Date(date);
    fDate.setSeconds(0, 0);
    const sortedEvents: ShareholderEvent[] = Events.sortEventsByDate(events);
    return this.replayToDate(sortedEvents, fDate);
  }

  public static getVolumeForShareclass(event: ShareholderEvent, shareclass: ShareClass): number {
    switch (event.type) {
      case ShareholderEventType.emission:
      case ShareholderEventType.establish:
      case ShareholderEventType.import:
        return (event as ShareholderWithStateEvent).state.records.reduce((acc, cur) => {
          if (cur.shareclassId.toString() === shareclass._id.toString()) {
            return acc + cur.amount;
          } else {
            return acc;
          }
        }, 0);
      case ShareholderEventType.transaction:
        if ((event as TransactionEvent).shareclassId.toString() === shareclass._id.toString()) {
          return (event as TransactionEvent).amount;
        } else {
          return 0;
        }
      default:
        return 0;
    }
  }

  public static generateCompanyValueHistory(
    events: ShareholderEvent[],
    shareclasses: ShareClass[]
  ): Map<ShareClassId, ValueHistoryElement[]> {
    const sortedEvents: ShareholderEvent[] = Events.sortEventsByDate(events);

    const shareclassValueHistory: Map<ShareClassId, ValueHistoryElement[]> = new Map<
      ShareClassId,
      ValueHistoryElement[]
    >();

    let state: State = new State();

    for (const event of sortedEvents) {
      const amountOfShares = this.getAmountOfShares(state);
      state = Events.applyEventToState(event, state);

      const newAmountOfShares = this.getAmountOfShares(state);
      const { changedPrice, newPrice } = this.checkPriceHasChanged(shareclassValueHistory, event);

      const amountOfSharesChanged = amountOfShares !== newAmountOfShares;

      if (amountOfSharesChanged || changedPrice) {
        for (const shareclass of shareclasses) {
          const isEventRelatedToShareclass = this.isEventRelatedToShareclass(
            event,
            shareclass._id?.toString()
          );
          const amountOfSharesForShareclass = this.getAmountOfSharesForShareclass(
            state,
            shareclass._id?.toString()
          );

          const price = isEventRelatedToShareclass
            ? newPrice
            : this.getCurrentPriceForShareclass(shareclassValueHistory, shareclass._id?.toString());

          const date: Date = new Date(event.date || event.createdAt);
          const volume: number = Events.getVolumeForShareclass(event, shareclass);

          const eventSummary = {
            date: date,
            value: price && price * amountOfSharesForShareclass,
            price: price,
            volume: volume,
            shareAmount: amountOfSharesForShareclass,
          };

          const history = shareclassValueHistory.get(shareclass._id?.toString()) || [];
          history.push(eventSummary);
          shareclassValueHistory.set(shareclass._id?.toString(), history);
        }
      }
    }

    return shareclassValueHistory;
  }
  public static isEventRelatedToShareclass(event: ShareholderEvent, shareclassId: string) {
    if (![ShareholderEventType.emission, ShareholderEventType.moveShares].includes(event.type)) {
      return (event as any)?.shareclassId === shareclassId;
    } else if (event.type === ShareholderEventType.emission) {
      const emission = event as EmissionEvent;
      //Emissions only have one type of shareclass so the records confirm its type (i believe)
      return emission.state.records.some((record: ShareholderRecord) => {
        return record.shareclassId === shareclassId;
      });
    } else if (event.type === ShareholderEventType.moveShares) {
      const emission = event as moveSharesToClassEvent;
      //Emissions only have one type of shareclass so the records confirm its type (i believe)
      return emission?.toShareclassId === shareclassId;
    }
    return false;
  }
  public static getAmountOfShares(state: State) {
    return state.records.reduce((acc, cur) => {
      return acc + cur.amount;
    }, 0);
  }
  public static getAmountOfSharesForShareclass(state: State, shareclassId: string): number {
    return state.records.reduce((acc, cur) => {
      if (cur.shareclassId === shareclassId) {
        return acc + cur.amount;
      }
      return acc;
    }, 0);
  }
  public static checkPriceHasChanged(
    shareclassValueHistory: Map<string, ValueHistoryElement[]>,
    newEvent: ShareholderEvent
  ) {
    if (
      ![
        ShareholderEventType.split,
        ShareholderEventType.splice,
        ShareholderEventType.parvaluechange,
        ShareholderEventType.transaction,
        ShareholderEventType.emission,
        ShareholderEventType.establish,
        ShareholderEventType.moveShares,
      ].includes(newEvent.type)
    ) {
      return { changedPrice: false };
    }

    const shareclassId = (newEvent as any).shareclassId;
    const isIrrelevantForCompanyValue = newEvent.irrelevantForCompanyValue;
    if (isIrrelevantForCompanyValue) return { changedPrice: false };

    const history = shareclassValueHistory.get(shareclassId) || [{ price: -1 }];
    const lastElement = history[history.length - 1];

    let previousPrice = lastElement?.price || undefined;
    let newPrice = previousPrice;
    if (
      newEvent.type === ShareholderEventType.split ||
      newEvent.type === ShareholderEventType.splice
    ) {
      const event = newEvent as SplitEvent;
      newPrice = previousPrice / (event.to / event.from);
    } else if (newEvent.type === ShareholderEventType.parvaluechange) {
      const event = newEvent as ShareParvalueChangeEvent;
      newPrice = event.parValue;
    } else if (newEvent.type === ShareholderEventType.transaction) {
      const event = newEvent as TransactionEvent;
      newPrice = event.price;
    } else if (newEvent.type === ShareholderEventType.emission) {
      const event = newEvent as EmissionEvent;
      newPrice = event.price;
    } else if (newEvent.type === ShareholderEventType.moveShares) {
      const event = newEvent as moveSharesToClassEvent;
      //when moving shares we want to keep the price of the shareclass that is being moved to, if it is not set we use the price of the shareclass that is being moved from
      const historyOfSharedClassBeingMovedTo = shareclassValueHistory.get(event.toShareclassId) || [
        { price: -1 },
      ];
      const lastElementInHistoryOfShareclassBeingMovedTo =
        historyOfSharedClassBeingMovedTo[historyOfSharedClassBeingMovedTo.length - 1];
      const oldPriceOfShareclassBeingMovedTo =
        lastElementInHistoryOfShareclassBeingMovedTo?.price || previousPrice;
      if (oldPriceOfShareclassBeingMovedTo === previousPrice) previousPrice = undefined;

      newPrice = oldPriceOfShareclassBeingMovedTo; // if the price is not set, we use the previous price
    } else if (newEvent.type === ShareholderEventType.establish) {
      const event = newEvent as EstablishEvent;
      newPrice = event.price;
    }
    return { changedPrice: newPrice !== previousPrice, newPrice };
  }
  public static getCurrentPriceForShareclass(
    shareclassValueHistory: Map<string, ValueHistoryElement[]>,
    shareclassId: string
  ) {
    const history = shareclassValueHistory.get(shareclassId) || [];
    const lastElement = history[history.length - 1];
    return lastElement ? lastElement.price : undefined;
  }

  private static sameDate(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  public static removeEmptyRecords(x: ShareholderRecord[]): ShareholderRecord[] {
    return x.filter((y) => y.amount !== 0);
  }

  public static applyEventToState(event: ShareholderEvent, state: State): State {
    let newState: State = { records: [] };

    switch (event.type) {
      case ShareholderEventType.import:
        newState = _import({ event: event as ImportEvent, previousState: state, silent: true }); // overwrite
        break;
      case ShareholderEventType.edit:
        //legacy
        newState = _edit({ event: event as EditEvent, previousState: state, silent: true });
        break;
      // Emissions, and transactions etc. are similar to edits
      case ShareholderEventType.emission:
        newState = _emission({
          event: event as EmissionEvent,
          previousState: state,
          silent: true,
        });
        break;

      case ShareholderEventType.establish:
        newState = _establish({
          event: event as EstablishEvent,
          previousState: state,
          silent: true,
        });
        break;
      case ShareholderEventType.transaction:
        newState = _transaction({
          event: event as TransactionEvent,
          previousState: state,
          silent: true,
        });

        break;
      case ShareholderEventType.split:
        newState = _split({ event: event as SplitEvent, previousState: state, silent: true });
        break;
      case ShareholderEventType.splice:
        newState = _splice({ event: event as SpliceEvent, previousState: state, silent: true });
        break;
      case ShareholderEventType.sharedelete:
        newState = _delete({
          event: event as ShareDeletionDataEvent,
          previousState: state,
          silent: true,
        });
        break;
      case ShareholderEventType.moveShares:
        newState = _moveSharesFromShareclass({
          event: event as moveSharesToClassEvent,
          previousState: state,
          silent: true,
        });
        break;
      case ShareholderEventType.refreshShareNumbers:
        newState = _refreshShareNumbers({
          event: event as ShareholderEvent,
          previousState: state,
          silent: true,
        });
        break;

      //other events will just pass through
      default:
        newState = state;
        break;
    }

    return newState;
  }

  public static mergeEventsForUpdate(
    oldEvents: ShareholderEvent[],
    newEvents: ShareholderEvent[],
    dateThreshold: Date
  ): ShareholderEvent[] {
    const keepEvents: ShareholderEvent[] = oldEvents.map(
      (event: ShareholderEvent): ShareholderEvent => {
        if (new Date(event.date) >= dateThreshold) {
          return { ...event, deleted: true };
        }
        return event;
      }
    );
    return this.sortEventsByDate(keepEvents.concat(newEvents));
  }

  public static totalSharesBeforeDate(events: ShareholderEvent[], date: Date): number {
    const fDate: Date = new Date(date);
    //removed this line for the emission calc to work
    // fDate.setSeconds(0, 0);
    const sortedEvents: ShareholderEvent[] = Events.sortEventsByDate(events);
    const replayed: State = Events.replayToDate(sortedEvents, fDate);
    return replayed.records.reduce(
      (total: number, record: ShareholderRecord): number => record.amount + total,
      0
    );
  }
  public static recordBeforeDate(events: ShareholderEvent[], date: Date): ShareholderRecord[] {
    const fDate: Date = new Date(date);
    fDate.setSeconds(0, 0);
    const sortedEvents: ShareholderEvent[] = Events.sortEventsByDate(events);
    const replayed: State = Events.replayToDate(sortedEvents, fDate);
    //map to user total shares

    return replayed.records;
  }

  public static mergeEmissionPlanWithEvents(
    emissionPlan: EmissionPlan,
    eventsMap: { [key: string]: ShareholderEvent }
  ) {
    const event = eventsMap[emissionPlan.event || ''];
    if (event) {
      return {
        ...event,
        ...emissionPlan,
      };
    }
    return emissionPlan;
  }

  public static mergeEmissionPlansWithEvents(
    emissionPlans: EmissionPlan[],
    events: ShareholderEvent[]
  ) {
    const eventsMap = events.reduce((eventsMap: any, event: ShareholderEvent) => {
      eventsMap[event._id || ''] = event;
      return eventsMap;
    }, {});
    return emissionPlans.map((emissionPlan: EmissionPlan) =>
      Events.mergeEmissionPlanWithEvents(emissionPlan, eventsMap)
    );
  }
}

function precisionHelper(value) {
  return roundToPrecision(value, 6);
}
